import React, { useEffect, useState, useMemo } from 'react';
import { PlayArrow, Settings, Security, ContentCopy, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { ENDPOINTS, getAuthUrl, getPaymentUrl, getPaymentRequestByIdUrl, STANDARD_HEADERS } from '../../data/apiConfig';

const ApiPlayground = () => {
    const [activeEndpoint, setActiveEndpoint] = useState('auth');
    const [requestBody, setRequestBody] = useState('{"grant_type":"client_credentials"}');
    const [response, setResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [requestFormat, setRequestFormat] = useState('curl');
    const [copied, setCopied] = useState(false);

    const endpoints = useMemo(() => ({
        auth: {
            method: ENDPOINTS.auth.method,
            url: getAuthUrl(),
            description: ENDPOINTS.auth.description
        },
        payment: {
            method: ENDPOINTS.payment.method,
            url: getPaymentUrl(),
            description: ENDPOINTS.payment.description
        },
        paymentRequestById: {
            method: ENDPOINTS.paymentRequestById.method,
            url: getPaymentRequestByIdUrl('{id}'),
            description: ENDPOINTS.paymentRequestById.description
        }
    }), []);

    useEffect(() => {
        const handler = (e) => {
            const endpoint = e?.detail?.endpoint;
            if (!endpoint || !endpoints[endpoint]) return;
            setActiveEndpoint(endpoint);
        };
        window.addEventListener('netikash:playground', handler);
        return () => window.removeEventListener('netikash:playground', handler);
    }, [endpoints]);



    // Générer la requête selon le format
    const generatedRequest = useMemo(() => {
        const endpoint = endpoints[activeEndpoint];
        if (!endpoint) return '';

        const headers = STANDARD_HEADERS[activeEndpoint] || [];
        const hasBody = activeEndpoint === 'auth' || activeEndpoint === 'payment';

        try {
            switch (requestFormat) {
                case 'curl':
                    const curlHeaders = headers.map(h => `  -H "${h.name}: ${h.value}"`).join(' \\\n');
                    if (hasBody) {
                        return `curl -X ${endpoint.method} "${endpoint.url}" \\\n${curlHeaders} \\\n  -d '${requestBody}'`;
                    } else {
                        return `curl -X ${endpoint.method} "${endpoint.url}" \\\n${curlHeaders}`;
                    }
                case 'javascript':
                    const jsHeaders = headers.map(h => `      '${h.name}': '${h.value}'`).join(',\n');
                    if (hasBody) {
                        return `const axios = require('axios');\n\nconst response = await axios.${endpoint.method.toLowerCase()}('${endpoint.url}',\n  ${requestBody},\n  {\n    headers: {\n${jsHeaders}\n    }\n  }\n);\n\nconsole.log(response.data);`;
                    } else {
                        return `const axios = require('axios');\n\nconst response = await axios.${endpoint.method.toLowerCase()}('${endpoint.url}', {\n  headers: {\n${jsHeaders}\n  }\n});\n\nconsole.log(response.data);`;
                    }
                case 'python':
                    const pyHeaders = headers.map(h => `    '${h.name}': '${h.value}'`).join(',\n');
                    if (hasBody) {
                        return `import requests\n\nresponse = requests.${endpoint.method.toLowerCase()}('${endpoint.url}',\n  json=${requestBody},\n  headers={\n${pyHeaders}\n  }\n)\n\nprint(response.json())`;
                    } else {
                        return `import requests\n\nresponse = requests.${endpoint.method.toLowerCase()}('${endpoint.url}',\n  headers={\n${pyHeaders}\n  }\n)\n\nprint(response.json())`;
                    }
                default:
                    return '';
            }
        } catch (e) {
            return '// Erreur de génération';
        }
    }, [activeEndpoint, requestFormat, requestBody, endpoints]);


    // Codes d'erreur possibles
    const errorCodes = {
        auth: [
            { code: 400, message: 'Bad Request - Requête mal formée' },
            { code: 401, message: 'Unauthorized - Credentials invalides' },
            { code: 403, message: 'Forbidden - Accès refusé' },
            { code: 500, message: 'Internal Server Error - Erreur serveur' }
        ],
        payment: [
            { code: 400, message: 'Bad Request - Données invalides' },
            { code: 401, message: 'Unauthorized - Token invalide ou expiré' },
            { code: 403, message: 'Forbidden - Accès refusé' },
            { code: 422, message: 'Unprocessable Entity - Validation échouée' },
            { code: 500, message: 'Internal Server Error - Erreur serveur' }
        ],
        paymentRequestById: [
            { code: 400, message: 'Bad Request - Identifiant invalide' },
            { code: 401, message: 'Unauthorized - Token invalide ou expiré' },
            { code: 404, message: 'Not Found - Demande introuvable' },
            { code: 500, message: 'Internal Server Error - Erreur serveur' }
        ]
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(generatedRequest);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Erreur de copie:', err);
        }
    };

    const handleExecute = async () => {
        setIsLoading(true);
        // Simulation d'appel API
        setTimeout(() => {
            setResponse({
                status: 200,
                data: {
                    access_token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
                    expires_in: 3600,
                    token_type: 'Bearer'
                }
            });
            setIsLoading(false);
        }, 1000);
    };

    return (
        <div className="mt-8" id="api-playground">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Playground API</h3>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Settings className="text-lg" />
                    <span>Sandbox Mode</span>
                </div>
            </div>

            <div className="grid md:grid-cols-4 gap-6 mb-6">
                {Object.entries(endpoints).map(([key, endpoint]) => (
                    <button
                        key={key}
                        onClick={() => setActiveEndpoint(key)}
                        className={`p-4 rounded-lg border text-left transition-all ${
                            activeEndpoint === key
                                ? 'border-amber-500 bg-amber-50'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium mb-2 ${
                            endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                            {endpoint.method}
                        </div>
                        <div className="text-sm font-mono text-gray-600 truncate">{endpoint.url}</div>
                        <div className="text-xs text-gray-500 mt-1">{endpoint.description}</div>
                    </button>
                ))}
            </div>

            {/* Format de requête */}
            <div className="mb-4 flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Format:</span>
                {['curl', 'javascript', 'python'].map(format => (
                    <button
                        key={format}
                        onClick={() => setRequestFormat(format)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            requestFormat === format
                                ? 'bg-amber-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {format === 'curl' ? 'cURL' : format.charAt(0).toUpperCase() + format.slice(1)}
                    </button>
                ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Request */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">Requête générée</h4>
                        <button
                            onClick={copyToClipboard}
                            className="flex items-center space-x-1 text-amber-600 text-sm hover:text-amber-700"
                        >
                            {copied ? <CheckCircle fontSize="small" /> : <ContentCopy fontSize="small" />}
                            <span>{copied ? 'Copié !' : 'Copier'}</span>
                        </button>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-4 h-40 overflow-auto">
                        <pre className="text-sm text-green-400 font-mono">
                            <code>{generatedRequest || 'Sélectionnez un endpoint'}</code>
                        </pre>
                    </div>
                    {activeEndpoint === 'auth' || activeEndpoint === 'payment' ? (
                        <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Body (JSON)
                            </label>
                            <textarea
                                value={requestBody}
                                onChange={(e) => setRequestBody(e.target.value)}
                                className="w-full h-24 font-mono text-sm border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder='{"grant_type": "client_credentials"}'
                            />
                        </div>
                    ) : null}
                </div>

                {/* Response */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">Réponse</h4>
                        <button
                            onClick={handleExecute}
                            disabled={isLoading}
                            className="flex items-center space-x-2 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors"
                        >
                            <PlayArrow className="text-base" />
                            <span>{isLoading ? 'Execution...' : 'Tester'}</span>
                        </button>
                    </div>
                    <div className="h-40 bg-gray-900 text-gray-200 rounded-lg p-4 overflow-auto">
            <pre className="text-sm">
              {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                  </div>
              ) : response ? (
                  <code>{JSON.stringify(response, null, 2)}</code>
              ) : (
                  <span className="text-gray-400">La réponse s'affichera ici</span>
              )}
            </pre>
                    </div>
                </div>
            </div>

            {/* Headers requis */}
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Headers requis</h4>
                <div className="space-y-2">
                    {(STANDARD_HEADERS[activeEndpoint] || []).map((header, idx) => (
                        <div key={idx} className="flex items-start space-x-3 text-sm">
                            <code className="px-2 py-1 bg-white border border-gray-200 rounded font-mono text-xs">
                                {header.name}
                            </code>
                            <span className="text-gray-600">: {header.value}</span>
                            {header.required && (
                                <span className="text-xs text-red-600 font-medium">*requis</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Codes d'erreur possibles */}
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                    <ErrorIcon fontSize="small" />
                    <span>Codes d'erreur possibles</span>
                </h4>
                <div className="grid md:grid-cols-2 gap-2">
                    {(errorCodes[activeEndpoint] || []).map((error, idx) => (
                        <div key={idx} className="flex items-center space-x-2 text-sm">
                            <code className="px-2 py-1 bg-white border border-red-200 rounded font-mono text-xs font-semibold text-red-700">
                                {error.code}
                            </code>
                            <span className="text-red-800">{error.message}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Conseils de sécurité */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <Security fontSize="small" />
                    <span>Conseils de sécurité</span>
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Ne jamais exposer vos clés secrètes dans le code client</li>
                    <li>• Utilisez des variables d'environnement pour les credentials</li>
                    <li>• Validez toujours les réponses du serveur</li>
                    <li>• Implémentez la gestion des erreurs</li>
                </ul>
            </div>
        </div>
    );
};

export default ApiPlayground;